/**
 * The keyboard hint footer, and the `?` overlay behind it — §15.1.
 *
 * A shortcut nobody knows about saves nobody any clicks, and the usual fix — an
 * overlay you have to already know to press `?` for — has the same problem one
 * level down. So the bar is always there, quiet, naming the keys that would
 * actually do something to the row in front of the rep.
 *
 * Two deliberate choices:
 *
 *   - It renders IN FLOW, as a `flex: none` child of `.sdr-area`, not fixed to
 *     the viewport. The draft pane's Approve bar is `position: sticky; bottom: 0`,
 *     so a fixed footer would paint straight over the primary action at short
 *     window heights. In flow, the panes shrink by the bar's height and sticky
 *     resolves above it — which also means no z-index token is involved.
 *   - It lists only what the current selection can actually do. The registry
 *     reports what the mounted pane rendered, so a read-only row does not
 *     advertise an Approve that would refuse — the same rule the rest of this
 *     surface follows about not showing controls that lie.
 */

import { useState } from 'react';
import { Dialog } from './Dialog';
import { useQueueHints } from './queueActions';
import { tip } from './tips';

/** The navigation keys, which are always live — they belong to the list itself. */
const NAV_HINTS = [
  { keyLabel: 'J', label: 'next' },
  { keyLabel: 'K', label: 'previous' },
];

function Hint({ keyLabel, label }: { keyLabel: string; label: string }) {
  return (
    <span className="hint">
      <kbd>{keyLabel}</kbd>
      {label}
    </span>
  );
}

const TOUR_KEY = 'actos.queueTour';
/** Three launches, then it stops offering itself. Long enough to still be
 *  there on the second morning, short enough never to become furniture. */
const TOUR_LAUNCHES = 3;

/**
 * Where the launch count lives.
 *
 * An injectable seam rather than a bare `window.localStorage` call, because the
 * tests in this repo are pure-function and run with no DOM. Nothing could reach
 * this logic before, which is how the dismissal bug below survived: the
 * `resetQueueTourForTests` seam was exported and never once called.
 */
export interface TourStore {
  get(): string | null;
  set(value: string): void;
}

/** The real one. `window` is touched inside the methods and never at module
 *  load, so importing this file under Node does not throw. */
const browserStore: TourStore = {
  get: () => window.localStorage.getItem(TOUR_KEY),
  set: (value) => window.localStorage.setItem(TOUR_KEY, value),
};

/**
 * The first-run coach marks — R14 of the SDR backlog.
 *
 * The queue is three moves — pick a row, read what it will send, approve it —
 * and nothing on screen says so. A rep's first session is spent working that
 * out, and the parts they do not stumble across (the keyboard, that approving
 * schedules rather than sends) they simply never learn. Every competitor
 * onboards this surface; ActOS dropped someone into it.
 *
 * A strip, not a modal with a spotlight. A modal would block the very queue it
 * is describing, and the three steps are one sentence each — there is nothing
 * here worth interrupting someone for.
 *
 * It counts LAUNCHES rather than dismissals, so it is still there on the second
 * morning for someone who closed it in a hurry, and gone by the fourth whether
 * or not they ever read it. `localStorage` for the same reason the auto-advance
 * preference uses it: this is a habit of a person at a machine, not a business
 * rule that deserves a column and a migration.
 */
/**
 * Counted once per page load, not once per mount.
 *
 * The component unmounts whenever the rep switches to Replies and remounts on
 * the way back, so a mount counter spends all three showings inside the first
 * ten minutes — on the one visit where the rep is most likely to be exploring
 * the tabs. Module state is the natural scope: it lives exactly as long as the
 * page does, which is what "a launch" means here.
 */
let countedThisLoad: number | null = null;

export function recordLaunch(store: TourStore = browserStore): number {
  if (countedThisLoad !== null) return countedThisLoad;
  try {
    const seen = Number(store.get() ?? '0');
    const next = Number.isFinite(seen) ? seen + 1 : TOUR_LAUNCHES + 1;
    store.set(String(next));
    countedThisLoad = next;
  } catch {
    // A browser that will not store it gets no tour rather than one on every
    // load — the second is far more annoying than missing the first.
    countedThisLoad = TOUR_LAUNCHES + 1;
  }
  return countedThisLoad;
}

/**
 * Retire the tour for good.
 *
 * Updating the memo FIRST is the whole fix. `recordLaunch` answers out of
 * `countedThisLoad` for the rest of the page load and never consults storage
 * again, so a dismissal that only reached `localStorage` was invisible until a
 * reload. `QueueTour` mounts inside App's `view === 'queue'` branch, so leaving
 * for Replies or Team unmounted it, and coming back remounted it with
 * `dismissed` freshly `false` and `launches` re-read from the STALE memo. The
 * strip came back on every Queue round trip however many times the rep pressed
 * "Got it", and only a reload made the dismissal appear to take — reported from
 * the live app as "the Got it option does not save".
 */
export function dismissTour(store: TourStore = browserStore): void {
  countedThisLoad = TOUR_LAUNCHES + 1;
  try {
    store.set(String(TOUR_LAUNCHES + 1));
  } catch {
    // Dismissed for this page load is still dismissed: the memo above holds it
    // whether or not the write lands, so a locked-down browser profile stops
    // being nagged until the next reload rather than never.
  }
}

/** The render decision, split out so it can be tested without a DOM. */
export function tourVisible(launches: number, dismissed: boolean): boolean {
  return !dismissed && launches <= TOUR_LAUNCHES;
}

/** Test seam — module state has no page reload to clear it between cases. */
export function resetQueueTourForTests(): void {
  countedThisLoad = null;
}

export function QueueTour({ onShowHelp }: { onShowHelp: () => void }) {
  const [launches] = useState<number>(() => recordLaunch());

  const [dismissed, setDismissed] = useState(false);

  if (!tourVisible(launches, dismissed)) return null;

  /**
   * `setDismissed` is still needed alongside `dismissTour`: the module memo is
   * what hides the strip on the NEXT mount, but only React state repaints the
   * one currently on screen.
   */
  const stop = () => {
    setDismissed(true);
    dismissTour();
  };

  return (
    <div className="queue-tour" role="note" aria-label="How this queue works">
      <ol className="tour-steps">
        <li>
          <strong>1 · Pick a row.</strong> The list on the left is today, in priority order.
        </li>
        <li>
          <strong>2 · Read it.</strong> The pane shows exactly what the recipient receives, with the
          guards that checked it.
        </li>
        <li>
          <strong>3 · Approve.</strong> That schedules the send inside their working hours. It does
          not send now, and Skip can still pull it back.
        </li>
      </ol>
      <div className="tour-actions">
        <button
          type="button"
          className="link-btn"
          {...tip('Lists every keyboard shortcut for the queue. Press ? at any time.')}
          onClick={onShowHelp}
        >
          Keyboard shortcuts
        </button>
        <button
          type="button"
          className="link-btn"
          {...tip('Hides this for good. Everything it says is also in the ? shortcuts overlay.')}
          onClick={stop}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

export function QueueHints({ onShowHelp }: { onShowHelp: () => void }) {
  const actionHints = useQueueHints();

  return (
    /*
     * Not `role="toolbar"` and not `aria-live`: this is reference text, not a
     * control group, and announcing it on every selection change would talk over
     * the rep. The `?` overlay is the accessible route to the same content.
     */
    <div className="queue-hints">
      {NAV_HINTS.map((hint) => (
        <Hint key={hint.keyLabel} {...hint} />
      ))}
      {actionHints.map((hint) => (
        <Hint key={hint.keyLabel} {...hint} />
      ))}
      <button
        type="button"
        className="hint-more"
        {...tip('Lists every keyboard shortcut for the queue. Press ? at any time.')}
        onClick={onShowHelp}
      >
        <kbd>?</kbd>shortcuts
      </button>
    </div>
  );
}

/** Key, what it does, and — where it matters — what it does NOT do. */
const REFERENCE: ReadonlyArray<{ keys: string; what: string; note?: string }> = [
  { keys: 'J / ↓', what: 'Select the next item' },
  { keys: 'K / ↑', what: 'Select the previous item' },
  {
    keys: 'Space / Enter',
    what: 'The primary action on the selected item',
    note: 'Approve, where the item has one. Nothing at all on a read-only row.',
  },
  { keys: 'E', what: 'Switch between Preview and Edit' },
  { keys: 'R', what: 'Open the regenerate steer box' },
  { keys: 'S', what: 'Snooze for three days' },
  {
    keys: 'X',
    what: 'Skip',
    note: 'Abandons the step and cancels its queued send.',
  },
  { keys: '?', what: 'This list' },
  { keys: 'Esc', what: 'Close a panel or dialog' },
];

export function ShortcutOverlay({
  autoAdvance,
  onAutoAdvanceChange,
  onClose,
}: {
  autoAdvance: boolean;
  onAutoAdvanceChange: (next: boolean) => void;
  onClose: () => void;
}) {
  return (
    <Dialog onClose={onClose} label="Keyboard shortcuts" variant="modal">
      <div className="shortcut-help">
        <h2>Keyboard shortcuts</h2>
        <p className="fine">
          Shortcuts are inert while you are typing in a field, and while a dialog is open.
        </p>
        <dl>
          {REFERENCE.map((row) => (
            <div key={row.keys} className="shortcut-row">
              <dt>
                <kbd>{row.keys}</kbd>
              </dt>
              <dd>
                {row.what}
                {row.note && <span className="fine"> {row.note}</span>}
              </dd>
            </div>
          ))}
        </dl>

        {/*
          The toggle lives here rather than in Settings: everything in Settings is
          a server-persisted business rule, and this is a keyboard habit stored
          per device. It also sits where a rep who just learned the shortcut can
          turn its side effect off.
        */}
        <label className="shortcut-toggle">
          <input
            type="checkbox"
            checked={autoAdvance}
            onChange={(e) => onAutoAdvanceChange(e.target.checked)}
          />
          <span>
            Move to the next item after Approve
            <span className="fine"> Remembered on this device only.</span>
          </span>
        </label>

        <div className="draft-actions">
          <button type="button" className="primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Dialog>
  );
}
